import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import {
  Grid, TextField,
  FormControlLabel, Checkbox, Typography, Box, Divider, Chip, useTheme, alpha
} from '@mui/material';
import { supabase } from '../../../../supabaseClient';

const SamplePullStep = forwardRef(({ currentStep, formulation }, ref) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    analytical_portion: '',
    reserve_portion: '',
    stability_portion: '',
    sampling_strategy: '',
    sampling_tools: '',
    sampling_locations: '',
    sample_combination: '',
    container_material: '',
    sterility_requirement: '',
    storage_temperature: '',
    special_storage: '',
    sampler_name: '',
    analyst_name: '',
    tracking_method: '',
    transfer_method: '',
    label_information: '',
    retention_time: '',
    testing_timeline: '',
    handling_instructions: '',
    homogeneity_check: false,
    visual_inspection: false,
    temperature_check: false,
    duplicate_samples: false
  });

  // Load existing sample details when component mounts
  useEffect(() => {
    if (currentStep?.sample_details && Object.keys(currentStep.sample_details).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...currentStep.sample_details
      }));
    }
  }, [currentStep?.id]);

  // Expose save function to parent via ref
  useImperativeHandle(ref, () => ({
    saveData: async () => {
      if (!currentStep?.id) return false;

      try {
        const { error } = await supabase
          .from('procedure_steps')
          .update({
            sample_details: formData,
            updated_at: new Date().toISOString()
          })
          .eq('id', currentStep.id);

        if (error) throw error;
        return true;
      } catch (error) {
        console.error('Error saving sample data:', error);
        return false;
      }
    },
    formData: formData
  }), [formData, currentStep?.id]);

  // All field handlers - ONLY update local state, no database saves
  const handleAnalyticalPortionChange = (e) => {
    setFormData(prev => ({...prev, analytical_portion: e.target.value}));
  };

  const handleReservePortionChange = (e) => {
    setFormData(prev => ({...prev, reserve_portion: e.target.value}));
  };

  const handleStabilityPortionChange = (e) => {
    setFormData(prev => ({...prev, stability_portion: e.target.value}));
  };

  const handleSamplingStrategyChange = (e) => {
    setFormData(prev => ({...prev, sampling_strategy: e.target.value}));
  };

  const handleSamplingToolsChange = (e) => {
    setFormData(prev => ({...prev, sampling_tools: e.target.value}));
  };

  const handleSamplingLocationsChange = (e) => {
    setFormData(prev => ({...prev, sampling_locations: e.target.value}));
  };

  const handleSampleCombinationChange = (e) => {
    setFormData(prev => ({...prev, sample_combination: e.target.value}));
  };

  const handleContainerMaterialChange = (e) => {
    setFormData(prev => ({...prev, container_material: e.target.value}));
  };

  const handleSterilityRequirementChange = (e) => {
    setFormData(prev => ({...prev, sterility_requirement: e.target.value}));
  };

  const handleStorageTemperatureChange = (e) => {
    setFormData(prev => ({...prev, storage_temperature: e.target.value}));
  };

  const handleSpecialStorageChange = (e) => {
    setFormData(prev => ({...prev, special_storage: e.target.value}));
  };

  const handleSamplerNameChange = (e) => {
    setFormData(prev => ({...prev, sampler_name: e.target.value}));
  };

  const handleAnalystNameChange = (e) => {
    setFormData(prev => ({...prev, analyst_name: e.target.value}));
  };

  const handleTrackingMethodChange = (e) => {
    setFormData(prev => ({...prev, tracking_method: e.target.value}));
  };

  const handleTransferMethodChange = (e) => {
    setFormData(prev => ({...prev, transfer_method: e.target.value}));
  };

  const handleLabelInformationChange = (e) => {
    setFormData(prev => ({...prev, label_information: e.target.value}));
  };

  const handleRetentionTimeChange = (e) => {
    setFormData(prev => ({...prev, retention_time: e.target.value}));
  };

  const handleTestingTimelineChange = (e) => {
    setFormData(prev => ({...prev, testing_timeline: e.target.value}));
  };

  const handleHandlingInstructionsChange = (e) => {
    setFormData(prev => ({...prev, handling_instructions: e.target.value}));
  };

  const handleHomogeneityCheckChange = (e) => {
    setFormData(prev => ({...prev, homogeneity_check: e.target.checked}));
  };

  const handleVisualInspectionChange = (e) => {
    setFormData(prev => ({...prev, visual_inspection: e.target.checked}));
  };

  const handleTemperatureCheckChange = (e) => {
    setFormData(prev => ({...prev, temperature_check: e.target.checked}));
  };

  const handleDuplicateSamplesChange = (e) => {
    setFormData(prev => ({...prev, duplicate_samples: e.target.checked}));
  };

  const calculateTotalSamples = () => {
    const analytical = parseFloat(formData?.analytical_portion || 0);
    const reserve = parseFloat(formData?.reserve_portion || 0);
    const stability = parseFloat(formData?.stability_portion || 0);
    return analytical + reserve + stability;
  };

  return (
    <Box sx={{ 
      p: 4,
      background: `linear-gradient(135deg, ${alpha('#f8f9fa', 0.8)}, ${alpha('#e9ecef', 0.6)})`,
      backdropFilter: 'blur(10px)',
      borderRadius: 4,
      boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
    }}>
      <Box sx={{ 
        mb: 4,
        p: 4,
        borderRadius: 4,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.04)})`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.2)}`
        }
      }}>
        <Typography variant="h5" sx={{ 
          fontWeight: 500, 
          color: theme.palette.primary.main, 
          mb: 1,
          textAlign: 'center'
        }}>
          Sample Pull Requirements & Protocol
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ 
          textAlign: 'center',
          fontWeight: 500
        }}>
          Define comprehensive sampling procedures and documentation requirements
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Sample Planning Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)}, ${alpha(theme.palette.info.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.info.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.info.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.info.main, 
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              Sample Planning - How much do you need?
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="For immediate testing"
                  fullWidth
                  value={formData.analytical_portion}
                  onChange={handleAnalyticalPortionChange}
                  placeholder="50g"
                  size="small"
                  helperText="Amount needed for all tests"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.info.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="For backup/retest"
                  fullWidth
                  value={formData.reserve_portion}
                  onChange={handleReservePortionChange}
                  placeholder="100g"
                  size="small"
                  helperText="In case you need to repeat tests"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.info.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="For stability studies"
                  fullWidth
                  value={formData.stability_portion}
                  onChange={handleStabilityPortionChange}
                  placeholder="200g"
                  size="small"
                  helperText="For long-term testing"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.info.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ 
                  p: 3, 
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)}, ${alpha(theme.palette.success.main, 0.08)})`,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                  boxShadow: `0 4px 16px ${alpha(theme.palette.success.main, 0.1)}`,
                  backdropFilter: 'blur(8px)'
                }}>
                  <Typography variant="body2" sx={{ 
                    color: theme.palette.success.dark, 
                    fontWeight: 500,
                    mb: 0.5
                  }}>
                    Total Sample Needed: {calculateTotalSamples()}g
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                    Plan to take 10-20% extra to account for sampling losses
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Sampling Strategy Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)}, ${alpha(theme.palette.warning.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.warning.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.warning.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.warning.main, 
              mb: 3
            }}>
              Sampling Strategy - How to collect representative samples
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="How to pick samples from the batch?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.sampling_strategy}
                  onChange={handleSamplingStrategyChange}
                  placeholder="Top, middle, bottom for layers; Random locations for uniform batches; Every nth container for multiple units"
                  size="small"
                  helperText="Describe your sampling approach"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.warning.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="What tools to use for sampling?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.sampling_tools}
                  onChange={handleSamplingToolsChange}
                  placeholder="Sterile spatula for creams; Sample thief for powders; Dipper for liquids; Core sampler for solids"
                  size="small"
                  helperText="List the tools and equipment needed"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.warning.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="How many different spots to sample?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.sampling_locations}
                  onChange={handleSamplingLocationsChange}
                  placeholder="5"
                  size="small"
                  helperText="More locations = more representative"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.warning.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Mix samples together or keep separate?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.sample_combination}
                  onChange={handleSampleCombinationChange}
                  placeholder="Mix together for composite sample; Keep separate for individual analysis; Both methods"
                  size="small"
                  helperText="Explain your sample combination strategy"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.warning.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Container & Storage Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.primary.main, 
              mb: 3
            }}>
              Container & Storage - How to store your samples
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="What type of container?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.container_material}
                  onChange={handleContainerMaterialChange}
                  placeholder="Glass jar for most products; Plastic container (HDPE) for lightweight; Amber glass for light protection"
                  size="small"
                  helperText="Describe the container type and material"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Does it need to be sterile?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.sterility_requirement}
                  onChange={handleSterilityRequirementChange}
                  placeholder="Sterile container required; Just clean container; Aseptic technique for sterile sampling"
                  size="small"
                  helperText="Describe sterility requirements"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="How to store samples?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.storage_temperature}
                  onChange={handleStorageTemperatureChange}
                  placeholder="Room temperature (20-25°C); Refrigerator (2-8°C) for sensitive products; Freezer (-20°C) for long-term"
                  size="small"
                  helperText="Specify temperature and storage conditions"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Special storage needs?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.special_storage}
                  onChange={handleSpecialStorageChange}
                  placeholder="Protect from light; Keep dry (desiccated); Inert atmosphere (nitrogen); Store upright only"
                  size="small"
                  helperText="Any special storage requirements"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Chain of Custody Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.primary.main, 
              mb: 3
            }}>
              Chain of Custody - Tracking your samples
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Who is collecting the sample?"
                  fullWidth
                  value={formData.sampler_name}
                  onChange={handleSamplerNameChange}
                  placeholder="QC Technician, Production Operator"
                  size="small"
                  helperText="Person responsible for sampling"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Who is testing the sample?"
                  fullWidth
                  value={formData.analyst_name}
                  onChange={handleAnalystNameChange}
                  placeholder="Lab Analyst, QC Manager"
                  size="small"
                  helperText="Person who will perform tests"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="How to track samples?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.tracking_method}
                  onChange={handleTrackingMethodChange}
                  placeholder="Barcode labels for scanning; QR codes for smartphone tracking; RFID tags for electronic tracking"
                  size="small"
                  helperText="Describe sample tracking method"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="How to transfer samples?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.transfer_method}
                  onChange={handleTransferMethodChange}
                  placeholder="Hand delivery person to person; Sample cart for batch transport; Pneumatic tube for fast delivery"
                  size="small"
                  helperText="Describe transfer and delivery method"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Documentation & Labeling Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.primary.main, 
              mb: 3
            }}>
              Documentation & Labeling
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="What information goes on the label?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.label_information}
                  onChange={handleLabelInformationChange}
                  placeholder="Product name, Batch number, Sample date/time, Sampler initials, Storage conditions, Sample ID"
                  size="small"
                  helperText="Include all info needed to identify and handle the sample"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="How long to keep samples?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.retention_time}
                  onChange={handleRetentionTimeChange}
                  placeholder="6 months, 2 years, Until expiry + 1 year"
                  size="small"
                  helperText="Regulatory or company requirement"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="When to test samples?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.testing_timeline}
                  onChange={handleTestingTimelineChange}
                  placeholder="Immediately after sampling; Within 24 hours standard timeline; Within 48 hours extended; Weekly schedule for batch testing"
                  size="small"
                  helperText="Specify testing timeline requirements"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Special handling instructions"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.handling_instructions}
                  onChange={handleHandlingInstructionsChange}
                  placeholder="Keep upright, Shake gently before testing, Do not freeze, Warm to room temp before testing"
                  size="small"
                  helperText="Any special care needed for this product"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Quality Checks Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.primary.main, 
              mb: 3
            }}>
              Quality Checks
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.homogeneity_check}
                      onChange={handleHomogeneityCheckChange}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem'
                    }}>
                      Check that sample is uniform (no separation)
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.visual_inspection}
                      onChange={handleVisualInspectionChange}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem'
                    }}>
                      Visual inspection for obvious defects
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.temperature_check}
                      onChange={handleTemperatureCheckChange}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem'
                    }}>
                      Monitor temperature during sampling
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.duplicate_samples}
                      onChange={handleDuplicateSamplesChange}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem'
                    }}>
                      Collect duplicate samples for verification
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
});

SamplePullStep.displayName = 'SamplePullStep';

export default SamplePullStep; 